import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Fade,
  styled,
  IconButton,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface OptVerificationModalProps {
  open: boolean;
  onClose: () => void;
  contact: string;
  contactType: string;
  onVerifyOtp: (otp: string) => void;
  errorMessage:string;
  onResendOtp: () => void;
}

const ModalContainerBox = styled(Box)(({ theme }) => ({
  top: "51%",
  left: "51%",
  position: "absolute",
  transform: "translate(-50%, -50%)",
  boxShadow: theme.shadows[5],
  backgroundColor: theme.palette.background.paper,
  borderRadius: "20px",
  outline: "none",
  padding: '10px',
  width: "500px",
  '@media (min-width: 320px) and (max-width: 520px)': {
    width: "auto",
  },
  "& .innerBoxs": {
    flexDirection: 'column',
    display: 'flex',
    justify: 'center',
    alignItems: 'center',
    padding: '30px',
    '@media (min-width: 320px) and (max-width: 520px)': {
      padding: '10px',
    },
  },
  "& .modalTitles": {
    fontWeight: '600',
    lineHeight: '32px',
    fontSize: '28px',
    marginBottom: '19px',
    fontFamily: "Montserrat !important",
    color: '#0F172A',
    '@media (min-width: 320px) and (max-width: 520px)': {
      fontWeight: '600',
      lineHeight: '28px',
      fontSize: '22px',
    },
  },
  "& .modalDescriptions": {
    fontWeight: '400',
    fontSize: '16px',
    lineHeight: '22px',
    textAlign: 'center',
    color: '#64748B',
    fontFamily: "Montserrat !important",
    marginBottom: '25px',
    '@media (min-width: 320px) and (max-width: 510px)': {
      fontWeight: '400',
      fontSize: '14px',
      lineHeight: '18px',
    },
    '@media (max-width: 425px)': {
      width: "100%"
    }
  },
  "& .modalInputs": {
    display: "flex",
    gap: "10px",
    marginTop: "16px"
  },
  "& .textField": {
    marginBottom: '10px',
    '& div': {
      height: '65px',
      width: '65px',
      border: '1px solid #CBD5E1',
      borderRadius: '16px',
      '@media (max-width: 425px)': {
        height: '50px',
        width: '50px',
      }
    },
    '& input': {
      fontSize: '24px',
      fontWeight: '500',
      lineHeight: '32px',
      color: '#0F172A',
      textAlign: 'center',
      fontFamily: "Montserrat, sans-serif !important",
    },
  },
  "& .errorMessage": {
    color: "#E11B23",
    fontSize: '12px',
    fontWeight: 500,
  },
  "& .modalSubmitBtns": {
    fontSize: '16px !important',
    fontWeight: '600',
    height: '56px !important',
    width: '290px !important',
    lineHeight: '24px !important',
    color: '#fff !important',
    borderRadius: '30px',
    padding: '10px 16px !important',
    cursor: 'pointer',
    border: '1px solid currentColor',
    margin: '20px 0px',
    textTransform: 'initial',
    fontFamily: "Montserrat !important",
    background: 'linear-gradient(90deg, #8077FF 0%, #652784 60%)',
  },
  "& .modalResendOtpBtns": {
    fontWeight: '500',
    fontSize: '16px',
    color: '#652784',
    textTransform: 'initial',
    lineHeight: '22px',
    fontFamily: "Montserrat !important",
  },
}));

const OptVerificationModal: React.FC<OptVerificationModalProps> = ({
  open,
  onClose,
  contact,
  contactType,
  onVerifyOtp,
  errorMessage,
  onResendOtp
}) => {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (errorMessage) {
      setError(errorMessage);
    }
  }, [errorMessage])

  useEffect(() => {
    if (!open) {
      setOtp(["", "", "", ""]);
      setError("");
    }
  }, [open])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    setError("");
    const newOtp = [...otp];
    newOtp[index] = (e.target as HTMLInputElement).value.slice(0, 1);
    setOtp(newOtp);
    if ((e.target as HTMLInputElement).value.length === 1 && index < otp.length - 1) {
      document.getElementById(`otp-input-${index + 1}`)?.focus();
    }
  };

  const handleSubmit = () => {
    const otpValue = otp.join("");
    if (otpValue.length < 4) {
      setError("Please enter all OTP digits.");
      return;
    }
    onVerifyOtp(otpValue);
  };

  const handleClose = () => {
    setOtp(["", "", "", ""]);
    onClose();
    setError("")
  };

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open}>
        <ModalContainerBox>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton className="closeIcon" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box className="innerBoxs">
            <Typography component={"h2"} className="modalTitles">
              OTP Verification
            </Typography>
            <Typography className="modalDescriptions">
              Please enter the verification code sent to<br/>{contactType === 'email' ? `${contact}` : `+91 ${contact}`}
            </Typography>
            <Box className="modalInputs" sx={{ display: "flex" }}>
              {otp.map((digit, index) => (
                <TextField
                  className="textField"
                  key={index}
                  type="tel"
                  inputMode="numeric"
                  id={`otp-input-${index}`}
                  autoFocus={index === 0}
                  value={digit}
                  autoComplete="off"
                  onChange={(e) => handleChange(e, index)}
                  inputProps={{
                    maxLength: 1,
                    style: { textAlign: "center" },
                  }}
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
            {(error) && (
              <Typography className="errorMessage">
                {error}
              </Typography>
            )}
            <Box style={{ display: 'flex', flexDirection: 'column' }}>
              <Button className="modalSubmitBtns" type="submit" onClick={handleSubmit}>
                Submit
              </Button>
              <Button className="modalResendOtpBtns" type="submit" onClick={onResendOtp}>Resend OTP</Button>
            </Box>
          </Box>
        </ModalContainerBox>
      </Fade>
    </Modal>
  );
};

export default OptVerificationModal;
